/**
 * F7: el listado de facturas ancla al último cierre FINALIZADO de la sucursal
 * de la factura (punto_venta.sucursal).
 */

export function toDateOnlyYmd(fecha: unknown): string {
  if (typeof fecha === "string" && /^\d{4}-\d{2}-\d{2}/.test(fecha)) {
    return fecha.slice(0, 10);
  }
  if (fecha instanceof Date && !Number.isNaN(fecha.getTime())) {
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, "0");
    const day = String(fecha.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return "";
}

export type CierreListItem = {
  fecha?: unknown;
  cerrado?: boolean;
  sucursal?: unknown;
  sucursalId?: unknown;
  sucursal_id?: unknown;
};

export function sucursalIdOfCierre(cierre: CierreListItem): number | null {
  const raw = cierre.sucursalId ?? cierre.sucursal_id ?? cierre.sucursal;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Máxima fecha entre cierres con cerrado=true. Ignora abiertos. */
export function getLastClosedCierreYmd(
  cierres: CierreListItem[] = [],
): string | null {
  return getLastClosedCierreYmdForSucursal(cierres, null);
}

/** Último cierre FINALIZADO de esa sucursal (null sucursalId = todos). */
export function getLastClosedCierreYmdForSucursal(
  cierres: CierreListItem[] = [],
  sucursalId: number | null,
): string | null {
  let maxYmd: string | null = null;
  for (const cierre of cierres) {
    if (!cierre?.cerrado) continue;
    if (sucursalId != null && sucursalIdOfCierre(cierre) !== sucursalId) continue;
    const ymd = toDateOnlyYmd(cierre.fecha);
    if (!ymd) continue;
    if (!maxYmd || ymd > maxYmd) maxYmd = ymd;
  }
  return maxYmd;
}

export function filterFacturasByLastClosedCierre<T extends { fecha?: unknown }>(
  facturas: T[],
  lastClosedCierreYmd: string | null,
  showHistorial: boolean,
): T[] {
  if (!lastClosedCierreYmd) return facturas;

  if (showHistorial) {
    return facturas.filter(
      (factura) => toDateOnlyYmd(factura.fecha) === lastClosedCierreYmd,
    );
  }

  return facturas.filter(
    (factura) => toDateOnlyYmd(factura.fecha) > lastClosedCierreYmd,
  );
}

export function sucursalIdOfFactura(factura: {
  sucursalId?: unknown;
  sucursal_id?: unknown;
}): number | null {
  const raw = factura.sucursalId ?? factura.sucursal_id;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Cierre FINALIZADO de ESA sucursal en esa fecha. No usa cierres de otras sucursales. */
export function isCierreCerradoForSucursal(
  cierres: CierreListItem[] = [],
  ymd: string,
  sucursalId: number | null,
): boolean {
  if (!ymd || sucursalId == null) return false;
  return cierres.some(
    (cierre) =>
      Boolean(cierre?.cerrado) &&
      toDateOnlyYmd(cierre.fecha) === ymd &&
      sucursalIdOfCierre(cierre) === sucursalId,
  );
}

/** Filtra cada factura contra el último cierre FINALIZADO de SU sucursal. */
export function filterFacturasBySucursalClosedCierre<
  T extends { fecha?: unknown; sucursalId?: unknown; sucursal_id?: unknown },
>(
  facturas: T[],
  cierres: CierreListItem[],
  showHistorial: boolean,
): T[] {
  return facturas.filter((factura) => {
    const sucId = sucursalIdOfFactura(factura);
    const lastClosed = getLastClosedCierreYmdForSucursal(cierres, sucId);
    if (!lastClosed) return true;
    const ymd = toDateOnlyYmd(factura.fecha);
    if (showHistorial) return ymd === lastClosed;
    return ymd > lastClosed;
  });
}
